import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';
import { buildAttentionSubgraph, buildGQASubgraph } from './attention';
import { buildMlpSubgraph } from './mlp';

function buildAttn(g: any, inputId: string, prefix: string, params: Record<string, number | string>): string {
  const attnType = String(params.attention_type || 'mha');
  if (attnType === 'gqa') return buildGQASubgraph(g, inputId, prefix, params, false);
  if (attnType === 'mqa') return buildGQASubgraph(g, inputId, prefix, params, true);
  return buildAttentionSubgraph(g, inputId, prefix, params);
}

export const transformerBlockComponent: ComponentDef = {
  id: 'transformer-block',
  name: 'Transformer Block',
  description: 'A full Transformer block with configurable norm type, architecture variant, and parallel computation.',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Heads', type: 'number', default: 8, min: 1, max: 128, step: 1 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'd_ff', label: 'FFN dimension', type: 'number', default: 2048, min: 64, max: 16384, step: 64 },
    {
      name: 'activation',
      label: 'MLP activation',
      type: 'select',
      default: 'gelu',
      options: [
        { label: 'ReLU', value: 'relu' },
        { label: 'GELU', value: 'gelu' },
        { label: 'SiLU', value: 'silu' }
      ]
    },
    {
      name: 'norm_type',
      label: 'Norm type',
      type: 'select',
      default: 'LayerNorm',
      options: [
        { label: 'LayerNorm', value: 'LayerNorm' },
        { label: 'RMSNorm', value: 'RMSNorm' }
      ]
    },
    {
      name: 'variant',
      label: 'Architecture variant',
      type: 'select',
      default: 'pre-norm',
      options: [
        { label: 'Pre-Norm (GPT-2/LLaMA)', value: 'pre-norm' },
        { label: 'Post-Norm (original Transformer)', value: 'post-norm' },
        { label: 'Parallel (PaLM)', value: 'parallel' },
        { label: 'Sandwich-Norm (norm before & after sub-layer)', value: 'sandwich-norm' },
        { label: 'DeepNorm (α·x + SubLayer(x))', value: 'deepnorm' }
      ]
    },
    {
      name: 'attention_type',
      label: 'Attention',
      type: 'select',
      default: 'mha',
      options: [
        { label: 'MHA', value: 'mha' },
        { label: 'GQA', value: 'gqa' },
        { label: 'MQA', value: 'mqa' }
      ]
    },
    {
      name: 'mlp_variant',
      label: 'FFN',
      type: 'select',
      default: 'standard',
      options: [
        { label: 'Standard', value: 'standard' },
        { label: 'SwiGLU', value: 'swiglu' },
        { label: 'GeGLU', value: 'geglu' }
      ]
    }
  ],
  buildGraph: (params) => {
    const g = new GraphBuilder();
    const seq_len = Number(params.seq_len);
    const d_model = Number(params.d_model);
    const variant = String(params.variant || 'pre-norm');
    const normType = String(params.norm_type || 'LayerNorm');

    g.input('x', [seq_len, d_model]);

    if (variant === 'parallel') {
      // Single norm, attention and MLP run in parallel, outputs summed
      g.norm('norm1', 'x', normType);
      const attnOut = buildAttn(g, 'norm1', 'attn_', params);
      const mlpOut = buildMlpSubgraph(g, 'norm1', 'mlp_', params);
      g.add('parallel_sum', attnOut, mlpOut);
      g.add('out_add', 'x', 'parallel_sum');
      g.output('out', 'out_add');

    } else if (variant === 'post-norm') {
      // Original Transformer (Vaswani 2017): sublayer first, norm after residual
      const attnOut = buildAttn(g, 'x', 'attn_', params);
      g.add('add1', 'x', attnOut);
      g.norm('norm1', 'add1', normType);
      const mlpOut = buildMlpSubgraph(g, 'norm1', 'mlp_', params);
      g.add('add2', 'add1', mlpOut);
      g.norm('norm2', 'add2', normType);
      g.output('out', 'norm2');

    } else if (variant === 'sandwich-norm') {
      // Sandwich-Norm: norm before AND after each sub-layer, then residual add
      g.norm('norm1_pre', 'x', normType);
      const attnOut = buildAttn(g, 'norm1_pre', 'attn_', params);
      g.norm('norm1_post', attnOut, normType);
      g.add('add1', 'x', 'norm1_post');

      g.norm('norm2_pre', 'add1', normType);
      const mlpOut = buildMlpSubgraph(g, 'norm2_pre', 'mlp_', params);
      g.norm('norm2_post', mlpOut, normType);
      g.add('add2', 'add1', 'norm2_post');
      g.output('out', 'add2');

    } else if (variant === 'deepnorm') {
      // DeepNorm (Wang et al. 2022): scale residual by α before adding, enables very deep networks
      const alpha = 2.0;
      g.norm('norm1', 'x', normType);
      const attnOut = buildAttn(g, 'norm1', 'attn_', params);
      g.scale('alpha_x1', 'x', `α=${alpha} (residual scale)`);
      g.add('add1', 'alpha_x1', attnOut);
      g.norm('norm2', 'add1', normType);
      const mlpOut = buildMlpSubgraph(g, 'norm2', 'mlp_', params);
      g.scale('alpha_x2', 'add1', `α=${alpha} (residual scale)`);
      g.add('add2', 'alpha_x2', mlpOut);
      g.output('out', 'add2');

    } else {
      // Pre-Norm (default): norm before each sub-layer — more stable for large models
      g.norm('norm1', 'x', normType);
      const attnOut = buildAttn(g, 'norm1', 'attn_', params);
      g.add('add1', 'x', attnOut);
      g.norm('norm2', 'add1', normType);
      const mlpOut = buildMlpSubgraph(g, 'norm2', 'mlp_', params);
      g.add('add2', 'add1', mlpOut);
      g.output('out', 'add2');
    }

    return g.build();
  }
};
